import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePublicProject, useMiniAppRunner, useProjects, FormDataEntry } from '../hooks/useApi';
import { useTurnstile } from '../hooks/useTurnstile';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { AuthRequiredPrompt } from '../components/AuthRequiredPrompt';
import { Modal } from '../components/Modal';
import { InsufficientCreditsModal } from '../components/InsufficientCreditsModal';
import { CreditBalanceBadge } from '../components/CreditBalanceBadge';
import FrontendPreview, { EMBEDDING_BATCH_LIMITS, MINIAPP_MAX_PAYLOAD_BYTES } from '../components/FrontendPreview';
import { AlertCircle, Home, Crown, Wand2, ToggleLeft, ToggleRight, LogIn, LogOut, User, Shield, Users, Coins, Star, Sparkles, Menu, X } from 'lucide-react';
import { useUsage } from '../hooks/useUsage';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { SubscriptionTier, MonetizationMode, canAccessMiniApp } from '../lib/supabase';
import { Footer } from '../components/Footer';
import { useStarredProjects } from '../hooks/useStarredProjects';
import { CommentSection } from '../components/CommentSection';
import { SimilarMiniApps } from '../components/SimilarMiniApps';
import { ReportProblemButton } from '../components/ReportProblemButton';
import { useAnonymousCredits } from '../hooks/useAnonymousCredits';
import { useModelCreditCosts } from '../hooks/useModelCreditCosts';

export function PublicProjectPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { 
    project, 
    frontendHtml, 
    frontendFiles, 
    loading, 
    error,
    userFork,
    showingFork,
    setShowingFork,
    activeProjectId,
    toggleGuestAccess,
  } = usePublicProject(publicId || '');
  const { subscriptionTier, profileLoading, user, loading: authLoading, profile, signOut, session, isAdmin, authReady, ensureFreshToken } = useAuth();
  const { isStarred, toggleStar } = useStarredProjects();
  const { forkProject } = useProjects();
  
  // Turnstile for bot protection
  const { getToken: getTurnstileToken, TurnstileWidget, reset: resetTurnstile, error: turnstileError } = useTurnstile();
  
  // Use shared mini-app runner - use activeProjectId which is either fork or original
  // Pass Turnstile token getter for bot protection
  const { streaming, runMiniApp } = useMiniAppRunner(activeProjectId, session?.access_token, getTurnstileToken);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showForkModal, setShowForkModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [forkName, setForkName] = useState('');
  const [forking, setForking] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);
  const [togglingGuestAccess, setTogglingGuestAccess] = useState(false);
  const [avgDailyCredits, setAvgDailyCredits] = useState<number | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);

  // Fetch user's credit balance for pre-check before running the app
  const { data: usageData, loading: usageLoading } = useUsage();
  const { credits: anonymousCredits, loading: anonymousCreditsLoading } = useAnonymousCredits();
  const modelCreditCosts = useModelCreditCosts();
  const { locale } = useI18n();

  const hasFrontend = !!frontendHtml;
  const isFamilyFork = !!project?.family_params;
  const creditsRemaining = user
    ? (usageData?.credits?.totalRemaining ?? null)
    : anonymousCredits;

  // Fetch average daily credits per user for this mini-app
  useEffect(() => {
    if (!project?.id) return;
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    fetch(`${supabaseUrl}/rest/v1/rpc/get_avg_daily_credits_per_user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
      },
      body: JSON.stringify({ p_project_id: project.id }),
    })
      .then(res => res.json())
      .then(data => {
        if (typeof data === 'number') {
          setAvgDailyCredits(Math.max(4, Math.round(data)));
        } else {
          setAvgDailyCredits(4);
        }
      })
      .catch(() => setAvgDailyCredits(4));
  }, [project?.id]);
  
  // Set page title to mini-app's name
  useEffect(() => {
    if (project?.name) {
      document.title = `${project.name} - Llambada`;
    }
    return () => {
      document.title = 'Llambada';
    };
  }, [project?.name]);
  
  // Check if user has access to this app's required tier
  const requiredTier = project?.required_tier as SubscriptionTier | null;
  const monetizationMode = (project?.monetization_mode as MonetizationMode) || 'free';
  const requiresAuth = project?.requires_auth || false;
  const allowGuestAccess = project?.allow_guest_access || false;
  const effectiveTier = (!authLoading && user && !profileLoading) ? subscriptionTier : 'free';
  const isAuthenticated = !authLoading && !!user;
  
  // Guest mode only relaxes platform auth requirement (requires_auth).
  // It must not bypass creator monetization choice (e.g. paid_only).
  const effectiveRequiresAuth = allowGuestAccess ? false : requiresAuth;
  const accessCheck = canAccessMiniApp(
    isAuthenticated ? effectiveTier : null,
    monetizationMode,
    isAuthenticated,
    effectiveRequiresAuth
  );
  const hasAccess = accessCheck.canAccess;

  const handleFrontendSubmit = async (entries: FormDataEntry[]) => {
    setUploadError(null);

    // Credit pre-check: if we know the average spend and the user has fewer
    // credits than that, show the insufficient-credits modal instead of running.
    if (avgDailyCredits !== null) {
      // If credits are not loaded yet, defer to backend checks instead of
      // incorrectly blocking with a fallback 0.
      if (creditsRemaining !== null && creditsRemaining < avgDailyCredits) {
        setShowCreditsModal(true);
        return;
      }
    }

    const result = await runMiniApp(entries);
    if (result.error) {
      // Handle Turnstile errors
      if (result.errorCode === 'TURNSTILE_REQUIRED' || result.errorCode === 'TURNSTILE_FAILED') {
        // Reset Turnstile widget so it can generate a new token on retry
        resetTurnstile();

        // For guest-access apps, show a different message - just need to verify, not sign in
        if (allowGuestAccess) {
          setUploadError('Please complete the verification challenge below, then try again.');
        } else {
          setShowVerificationModal(true);
        }
      } else {
        setUploadError(result.error);
      }
    }
  };

  const handleForkProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !forkName.trim()) return;
    
    setForking(true);
    setForkError(null);
    
    const result = await forkProject(project.id, forkName.trim(), `Based on ${project.name}`);
    
    setForking(false);
    
    if (result.error) {
      setForkError(result.error);
      return;
    }
    
    // Navigate to the forked project
    if (result.data) {
      navigate(`/project/${result.data.id}`);
    }
  };

  const openForkModal = () => {
    if (project) {
      setForkName(`${project.name} (Custom)`);
      setShowForkModal(true);
    }
  };

  const handleForkClick = () => {
    if (user) {
      // User is logged in, open fork modal
      openForkModal();
    } else {
      // User is not logged in, redirect to sign-in page with return URL
      const returnUrl = window.location.pathname + window.location.search;
      navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    // Stay on the current page after sign out
  };

  const handleToggleGuestAccess = async () => {
    if (!project || togglingGuestAccess) return;
    
    setTogglingGuestAccess(true);
    const newValue = !project.allow_guest_access;
    const result = await toggleGuestAccess(newValue);
    setTogglingGuestAccess(false);
    
    if (result.error) {
      console.error('Failed to toggle guest access:', result.error);
    }
  };


  if (loading || !authReady) {
    return (
      <div className="min-h-screen bg-theme-bg bg-mesh flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" />
          <p className="text-theme-text-muted">{loading ? 'Loading project...' : 'Checking authentication...'}</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-theme-bg bg-mesh flex items-center justify-center p-4">
        <div className="card p-6 sm:p-8 max-w-md w-full text-center">
          <div className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-theme-error-bg mb-4 sm:mb-6">
            <AlertCircle className="h-7 w-7 sm:h-8 sm:w-8 text-theme-error" />
          </div>
          <h1 className="font-display text-xl sm:text-2xl font-bold text-theme-text mb-2">
            Mini-App not found
          </h1>
          <p className="text-sm sm:text-base text-theme-text-muted mb-6">
            {error || 'This mini-app does not exist or is not published.'}
          </p>
          <Link to="/" className="btn btn-primary">
            <Home className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col">
      {/* Header */}
      <header className="border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-3">
            {/* Left side - Logo and project info */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <Link 
                to="/"
                className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg shadow-lg shadow-accent-500/20 hover:opacity-90 transition-opacity shrink-0"
              >
                <img src="/logo.png" alt="Llambada" className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg" />
              </Link>
              <div className="min-w-0 flex-1">
                <h1 className="font-display text-sm sm:text-lg font-semibold text-theme-text truncate">
                  {showingFork && userFork ? userFork.name : project.name}
                </h1>
                <div className="hidden sm:flex items-center gap-1.5 text-[10px] sm:text-xs text-theme-text-muted truncate">
                  {project.creator_display_name && (
                    <>
                      <User className="h-3 w-3 shrink-0" />
                      {project.creator_is_public_profile && project.creator_username ? (
                        <Link
                          to={`/u/${project.creator_username}`}
                          className="truncate hover:text-accent-500 transition-colors underline-offset-2 hover:underline"
                          title={`View ${project.creator_display_name}'s profile`}
                        >
                          {project.creator_display_name}
                        </Link>
                      ) : (
                        <span className="truncate">{project.creator_display_name}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right side - Desktop actions */}
            <div className="hidden md:flex items-center gap-2 sm:gap-3 shrink-0">
              {/* Admin toggle for guest access */}
              {isAdmin && (
                <button
                  onClick={handleToggleGuestAccess}
                  disabled={togglingGuestAccess}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border transition-colors ${
                    project?.allow_guest_access
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20'
                      : 'bg-theme-surface border-theme-border-muted text-theme-text-secondary hover:bg-theme-surface-hover'
                  }`}
                  title={project?.allow_guest_access
                    ? 'Guest access enabled - click to disable'
                    : 'Guest access disabled - click to enable for non-authenticated users'
                  }
                >
                  {togglingGuestAccess ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Users className="h-3.5 w-3.5" />
                      {project?.allow_guest_access ? 'Guest: On' : 'Guest: Off'}
                    </>
                  )}
                </button>
              )}

              {requiredTier && (
                <span className="inline-flex items-center gap-1 text-xs text-theme-primary bg-theme-primary/10 px-2 py-1 rounded-full">
                  <Crown className="h-3 w-3" />
                  Paid
                </span>
              )}

              {user ? (
                <>
                  {project && (
                    <button
                      onClick={() => toggleStar(project.id)}
                      className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border transition-colors ${
                        isStarred(project.id)
                          ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20'
                          : 'bg-theme-surface border-theme-border-muted text-theme-text-secondary hover:bg-theme-surface-hover'
                      }`}
                      title={isStarred(project.id) ? 'Unstar this app' : 'Star this app'}
                    >
                      <Star className="h-3.5 w-3.5" fill={isStarred(project.id) ? 'currentColor' : 'none'} />
                      {isStarred(project.id) ? 'Starred' : 'Star'}
                    </button>
                  )}
                  <CreditBalanceBadge />
                  <ThemeToggle />
                  <Link to="/profile" className="btn btn-ghost text-sm" title="Profile & Settings">
                    <User className="h-4 w-4" />
                  </Link>
                  <button onClick={handleSignOut} className="btn btn-ghost text-sm" title="Sign out">
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <CreditBalanceBadge />
                  <ThemeToggle />
                  <Link
                    to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                    className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg bg-theme-surface hover:bg-theme-surface-hover transition-colors"
                    title="Sign in"
                  >
                    <LogIn className="h-4 w-4" />
                    Sign in
                  </Link>
                </>
              )}
            </div>

            {/* Right side - Mobile actions */}
            <div className="flex md:hidden items-center gap-2 shrink-0">
              {requiredTier && (
                <span className="inline-flex items-center gap-1 text-[10px] text-theme-primary bg-theme-primary/10 px-1.5 py-0.5 rounded-full">
                  <Crown className="h-2.5 w-2.5" />
                </span>
              )}
              <CreditBalanceBadge />
              <ThemeToggle />
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="btn btn-ghost p-2"
                aria-label="Toggle menu"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {showMobileMenu && (
            <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
              {user && project && (
                <button
                  onClick={() => { toggleStar(project.id); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left"
                >
                  <Star className="h-4 w-4" fill={isStarred(project.id) ? 'currentColor' : 'none'} />
                  {isStarred(project.id) ? 'Unstar this app' : 'Star this app'}
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { handleToggleGuestAccess(); setShowMobileMenu(false); }}
                  disabled={togglingGuestAccess}
                  className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left"
                >
                  <Users className="h-4 w-4" />
                  {project?.allow_guest_access ? 'Guest Access: On' : 'Guest Access: Off'}
                </button>
              )}
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <User className="h-4 w-4" />
                    Profile & Settings
                  </Link>
                  <button
                    onClick={() => { handleSignOut(); setShowMobileMenu(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </>
              ) : (
                <Link
                  to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <LogIn className="h-4 w-4" />
                  Sign in
                </Link>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {!hasAccess ? (
          accessCheck.reason === 'auth_required' ? (
            <AuthRequiredPrompt appName={project.name} requiresAuth={requiresAuth} />
          ) : (
            <UpgradePrompt requiredTier={requiredTier || 'standard_with_ads'} appName={project.name} />
          )
        ) : hasFrontend ? (
          <div className="flex flex-col bg-theme-surface">
            <div className="max-w-5xl mx-auto w-full px-4 pt-4">
              <nav className="flex items-center gap-2 text-xs sm:text-sm text-theme-text-muted overflow-hidden pb-3">
                <Link to="/" className="hover:text-accent-500 transition-colors whitespace-nowrap">
                  All apps
                </Link>
                <span>&gt;</span>
                <Link to="/utilities" className="hover:text-accent-500 transition-colors whitespace-nowrap">
                  Utilities
                </Link>
                <span>&gt;</span>
                <span className="text-theme-text truncate">{project.name}</span>
              </nav>

              {/* Description - level 2 */}
              {project.description && (
                <p className="py-3 text-center text-sm text-theme-text max-w-2xl mx-auto">
                  {showingFork && userFork ? `Based on ${project.name}` : project.description}
                </p>
              )}
              {/* Average daily credit usage */}
              {avgDailyCredits !== null && (
                <div className="flex justify-center pb-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs text-theme-text-secondary bg-theme-surface px-2 py-1 rounded-full" title="Average credits used per user per day">
                    <Coins className="h-3 w-3 text-theme-text-muted" />
                    Average number of credits consumed by users for that mini-app: ~{avgDailyCredits} credits/day
                  </span>
                </div>
              )}
            </div>
            {/* Preview - level 3 */}
            <div ref={previewContainerRef} className="relative max-w-5xl mx-auto w-full px-4 pb-4">
              <div className="rounded-xl shadow-lg border border-theme-border-muted overflow-hidden bg-white">
                {!isFamilyFork && (
                  <div className="flex justify-end px-3 pt-3 pb-2">
                    {userFork ? (
                      <button
                        onClick={() => setShowingFork(!showingFork)}
                        className="flex items-center gap-1.5 text-[10px] sm:text-xs px-2 py-1 rounded-lg bg-theme-surface border border-theme-border-muted shadow-sm hover:bg-theme-surface-hover transition-colors"
                        title={showingFork ? 'Show original version' : 'Show your customized version'}
                      >
                        {showingFork ? (
                          <ToggleRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent-500" />
                        ) : (
                          <ToggleLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-theme-text-muted" />
                        )}
                        <span className="hidden sm:inline">{showingFork ? 'Your Version' : 'Original'}</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleForkClick}
                        className="flex items-center gap-1.5 text-[10px] sm:text-xs px-2 py-1 rounded-lg bg-theme-surface border border-theme-border-muted shadow-sm hover:bg-theme-surface-hover transition-colors"
                        title={user ? "Customize this project" : "Sign in to customize this project"}
                      >
                        <Wand2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Customize</span>
                      </button>
                    )}
                  </div>
                )}
                <FrontendPreview
                  html={frontendHtml}
                  frontendFiles={frontendFiles}
                  onSubmit={handleFrontendSubmit}
                  isProcessing={streaming.status === 'authenticating' || streaming.status === 'running'}
                  isComplete={streaming.status === 'completed'}
                  backendError={streaming.status === 'failed' ? (streaming.error || 'Unknown error') : null}
                  publicMode={true}
                  projectId={activeProjectId}
                  appUrl={window.location.origin + window.location.pathname + window.location.search}
                  sessionToken={session?.access_token}
                  getSessionToken={ensureFreshToken}
                  getTurnstileToken={getTurnstileToken}
                  userContext={{
                    id: user?.id || null,
                    email: user?.email || null,
                    displayName: profile?.display_name || null,
                    tier: subscriptionTier,
                    locale,
                    creditsAvailable: user ? (usageData?.credits?.totalRemaining ?? 0) : (anonymousCredits ?? 0),
                    creditsLoaded: user ? (!usageLoading && usageData !== null) : !anonymousCreditsLoading,
                    creditCosts: modelCreditCosts,
                    maxPayloadBytes: MINIAPP_MAX_PAYLOAD_BYTES[subscriptionTier] ?? MINIAPP_MAX_PAYLOAD_BYTES.free,
                    embeddingBatchLimit: EMBEDDING_BATCH_LIMITS[subscriptionTier] ?? EMBEDDING_BATCH_LIMITS.free,
                  }}
                  familyConfig={project.family_params ? {
                    params: project.family_params,
                    description: project.description,
                    title: project.name,
                  } : null}
                  projectUpdatedAt={project.updated_at}
                />
                {(uploadError || turnstileError) && (
                  <div className="mt-3 flex items-center gap-2 text-theme-error text-xs sm:text-sm max-w-4xl mx-auto px-4">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span className="truncate">{uploadError || turnstileError}</span>
                  </div>
                )}
                {activeProjectId && (
                  <div className="flex justify-end px-3 pb-3 pt-1">
                    <ReportProblemButton projectId={activeProjectId} projectName={project.name} />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center animate-fade-in">
              <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-bold text-theme-text mb-4 sm:mb-6">
                {showingFork && userFork ? userFork.name : project.name}
              </h1>
              {project.creator_display_name && (
                <p className="text-sm text-theme-text-muted mb-4 flex items-center justify-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {project.creator_is_public_profile && project.creator_username ? (
                    <Link
                      to={`/u/${project.creator_username}`}
                      className="hover:text-accent-500 transition-colors underline-offset-2 hover:underline"
                      title={`View ${project.creator_display_name}'s profile`}
                    >
                      {project.creator_display_name}
                    </Link>
                  ) : (
                    <span>{project.creator_display_name}</span>
                  )}
                </p>
              )}
              {project.description && (
                <p className="text-base sm:text-xl text-theme-text-muted max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
                  {showingFork && userFork ? `Based on ${project.name}` : project.description}
                </p>
              )}
              <p className="text-sm sm:text-base text-theme-text-muted">
                This project doesn't have a frontend interface yet.
              </p>
              {/* Customize button for no-frontend view (hidden for family forks) */}
              {!isFamilyFork && !userFork && (
                <button
                  onClick={handleForkClick}
                  className="mt-6 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-theme-surface hover:bg-theme-surface-hover border border-theme-border-muted transition-colors"
                  title={user ? "Customize this project" : "Sign in to customize this project"}
                >
                  <Wand2 className="h-4 w-4" />
                  Customize
                </button>
              )}
              {!isFamilyFork && userFork && (
                <button
                  onClick={() => setShowingFork(!showingFork)}
                  className="mt-6 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-theme-surface hover:bg-theme-surface-hover border border-theme-border-muted transition-colors"
                  title={showingFork ? 'Show original version' : 'Show your customized version'}
                >
                  {showingFork ? (
                    <ToggleRight className="h-4 w-4 text-accent-500" />
                  ) : (
                    <ToggleLeft className="h-4 w-4 text-theme-text-muted" />
                  )}
                  {showingFork ? 'Your Version' : 'View Original'}
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Similar mini-apps — right below the iframe */}
      {hasAccess && hasFrontend && project?.id && (
        <SimilarMiniApps projectId={project.id} />
      )}

      {hasAccess && hasFrontend && (
        <section className="bg-theme-bg">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6 text-center">
            <Link
              to="/creators"
              className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs text-accent-500 hover:text-accent-400 font-medium transition-colors"
            >
              <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              Build your own mini-app
            </Link>
          </div>
        </section>
      )}


      {/* Comments Section */}
      {publicId && (
        <section className="border-t border-theme-border-muted bg-theme-bg">
          <CommentSection
            projectPublicId={publicId}
            projectAuthorUsername={project.creator_username}
          />
        </section>
      )}

      <Footer variant="full" />

      {/* Customize Modal */}
      <Modal
        isOpen={showForkModal}
        onClose={() => setShowForkModal(false)}
        title="Customize"
        size="sm"
      >
        <form onSubmit={handleForkProject} className="space-y-4">
          <p className="text-sm text-theme-text-muted">
            Create your own copy of "{project.name}" to customize. You can modify this mini-app to better suit your needs. No knowledge of computer programming is necessary.
          </p>
          <div>
            <label htmlFor="forkName" className="block text-sm font-medium text-theme-text-secondary mb-2">
              Name
            </label>
            <input
              id="forkName"
              type="text"
              value={forkName}
              onChange={(e) => setForkName(e.target.value)}
              className="input"
              placeholder="My customized project"
              required
              autoFocus
            />
          </div>
          {forkError && (
            <div className="text-sm text-theme-error bg-theme-error-bg border border-theme-error-border rounded-lg p-3">
              {forkError}
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowForkModal(false)}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={forking || !forkName.trim()}
              className="btn btn-primary flex-1"
            >
              {forking ? <LoadingSpinner size="sm" /> : 'Customize'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Verification Required Modal */}
      <Modal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        title="Sign In Required"
        size="sm"
      >
        <div className="text-center py-4">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-theme-primary/10 mb-4">
            <Shield className="h-8 w-8 text-theme-primary" />
          </div>
          <h3 className="text-lg font-semibold text-theme-text mb-2">
            Verification Required
          </h3>
          <p className="text-sm text-theme-text-muted mb-6">
            To protect against bots and abuse, this app requires you to sign in before use. 
            Creating an account is free and only takes a moment.
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}
              className="btn btn-primary w-full"
              onClick={() => setShowVerificationModal(false)}
            >
              <LogIn className="h-4 w-4 mr-2" />
              Sign In
            </Link>
            <Link
              to={`/register?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}
              className="btn btn-secondary w-full"
              onClick={() => setShowVerificationModal(false)}
            >
              Create Free Account
            </Link>
          </div>
        </div>
      </Modal>

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        isAuthenticated={!!user}
        creditsRemaining={creditsRemaining ?? 0}
        avgCreditsNeeded={avgDailyCredits ?? undefined}
      />

      {/* Turnstile widget for bot protection (invisible) */}
      <TurnstileWidget />
    </div>
  );
}
